import type { Organization } from '@noconflict/contracts';
import { useTranslation } from 'react-i18next';
import { useBillingProfile } from '../../entities/billing-profile';
import { useOrgContext } from '../../entities/org';
import { BillingProfileForm } from '../../features/org/billing-profile-form';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../shared/ui/dialog';
import { ErrorState } from '../../shared/ui/error-state';
import { Skeleton } from '../../shared/ui/skeleton';

/**
 * What Submit opens instead of the confirmation while the org's billing details are incomplete. Someone with
 * `billing.manage` completes them here (the settings form, prefilled from what is saved) and goes straight on;
 * anyone else is told who can. `order_submit` still refuses an incomplete profile: this only decides what shows.
 */
export function BillingStep({
  open,
  onOpenChange,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The details are saved; the caller goes on to its confirmation. */
  onSaved: () => void;
}) {
  const { t } = useTranslation(['order', 'errors']);
  const { org, can } = useOrgContext();
  const canManage = can('billing.manage');
  if (!canManage) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('panel.billingNeededTitle')}</DialogTitle>
            <DialogDescription>{t('errors:identity.billing_profile_incomplete')}</DialogDescription>
          </DialogHeader>
          <DialogFooter showCloseButton />
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('panel.billingTitle')}</DialogTitle>
          <DialogDescription>{t('panel.billingIntro')}</DialogDescription>
        </DialogHeader>
        {open ? (
          <SavedProfileForm org={org} onSaved={onSaved} onCancel={() => onOpenChange(false)} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/** The form takes its defaults once, so it waits for whatever is saved (partial details included). */
function SavedProfileForm({
  org,
  onSaved,
  onCancel,
}: {
  org: Organization;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('order');
  const profile = useBillingProfile(org.id);
  if (profile.isPending) {
    return (
      <div className="grid gap-4" role="status" aria-label={t('panel.billingLoading')}>
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-2/3" />
      </div>
    );
  }
  if (profile.isError) return <ErrorState size="compact" onRetry={() => profile.refetch()} />;
  return (
    <BillingProfileForm org={org} profile={profile.data} onSaved={onSaved} onCancel={onCancel} />
  );
}
